import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { promoCodesService } from './promo-codes.service';
import { PromoCode } from './promo-codes.dto';
import { useAuth } from '../../contexts/AuthContext';

const PromoCodes = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPromoCodes, setTotalPromoCodes] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoCodeToDelete, setPromoCodeToDelete] = useState<number | null>(
    null
  );
  const itemsPerPage = 10;

  // Set up logout callback for session expired
  useEffect(() => {
    promoCodesService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch promo codes from API
  const fetchPromoCodes = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await promoCodesService.getPromoCodes(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      });

      setPromoCodes(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalPromoCodes(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch promo codes'
      );
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setPromoCodeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !promoCodeToDelete) return;

    try {
      await promoCodesService.deletePromoCode(token, promoCodeToDelete);
      showSnackbar('Promo code deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setPromoCodeToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (promoCodes.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchPromoCodes();
      }
    } catch (err) {
      console.error('Error deleting promo code:', err);
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete promo code',
        'error'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPromoCodeToDelete(null);
  };

  const handleEdit = (id: number) => {
    navigate(`/promo-codes/edit/${id}`);
  };

  const handleAddPromoCode = () => {
    navigate('/promo-codes/add');
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Promo Codes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPromoCode}
          disabled={!isAuthenticated}
        >
          Add Promo Code
        </Button>
      </Box>

      {/* Search Bar */}
      {/* <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search promo codes..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box> */}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Promo Codes Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Min Order</TableCell>
                <TableCell>Max Discount</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading promo codes...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : promoCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <LocalOfferIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No promo codes found matching your search'
                        : 'No promo codes found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promoCodes.map((promoCode, index) => (
                  <TableRow key={promoCode.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {promoCode.promoCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {promoCode.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promoCode.discountType}
                        color={
                          promoCode.discountType === 'percentage'
                            ? 'primary'
                            : 'secondary'
                        }
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {promoCode.discountType === 'percentage'
                          ? `${Math.round(promoCode.discountValue)}%`
                          : `$${promoCode.discountValue}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ${promoCode.minimumOrderAmount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ${promoCode.maximumDiscount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {promoCode.usedCount || 0}/{promoCode.usageLimit || '∞'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(promoCode.startDate)} -{' '}
                        {formatDate(promoCode.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promoCode.status === 1 ? 'Active' : 'Inactive'}
                        color={promoCode.status === 1 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(promoCode.id)}
                          size="small"
                          disabled={loading}
                          title="Edit Promo Code"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(promoCode.id)}
                          size="small"
                          disabled={loading}
                          title="Delete Promo Code"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages} (Total: ${totalPromoCodes} promo codes)`}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this promo code? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromoCodes;
